import { useEffect, useRef, useState } from 'react';
import { MatchState } from '../types';
import { generateResultsJSON, sendResultsToCallback } from './useResultSender';
import { SendError, classifySendError } from '../errors/sendError';

interface UseTerminatedCallbackOptions {
  matchState: MatchState;
  /** True when the match has a callback URL to reach. */
  enabled: boolean;
  /** True once the result no longer needs the automatic send (the referee validated it). */
  stopped: boolean;
}

interface UseTerminatedCallbackReturn {
  /** The callback accepted the `terminated` result. */
  sent: boolean;
  /** The callback refused the result for good (401/403/4xx): retrying the same payload is pointless. */
  rejected: SendError | null;
}

/** Pace of the retries while the automatic result has not gone through. */
export const TERMINATED_RETRY_INTERVAL_MS = 15000;

/** Refusals that the same payload will meet again: no retry, the referee and the organizer must act. */
const isPermanent = (error: SendError) => error.kind === 'auth' || error.kind === 'client';

/**
 * Automatic `terminated` callback of the result screen. A single attempt was lost to a network drop at
 * the end of a match, and a paired tablet then leaves the result screen on its own. Sent as soon as the screen shows, then retried every 15 s and at once when the browser comes back
 * online, until the callback accepts it, refuses it for good, or the referee validates. One request at
 * a time. `online` only speeds things up: `navigator.onLine` stays true on a wifi without internet, the
 * interval does the real work.
 */
export function useTerminatedCallback({ matchState, enabled, stopped }: UseTerminatedCallbackOptions): UseTerminatedCallbackReturn {
  const [sent, setSent] = useState(false);
  const [rejected, setRejected] = useState<SendError | null>(null);
  // The latest match state, without restarting the loop each time it changes identity
  // (durations, announcement): it used to resend `terminated` several times
  const matchStateRef = useRef(matchState);
  matchStateRef.current = matchState;
  const doneRef = useRef(false);
  // Shared across effect runs: StrictMode's second mount must not start a parallel request,
  // and a request that outlives a cleanup still reports its outcome
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled || stopped || doneRef.current) return;

    const attempt = async () => {
      const url = matchStateRef.current.matchInfo.resultCallbackUrl;
      if (!url || inFlightRef.current || doneRef.current) return;
      inFlightRef.current = true;
      try {
        await sendResultsToCallback(generateResultsJSON(matchStateRef.current, 'terminated'), url);
        doneRef.current = true;
        setSent(true);
      } catch (error) {
        const sendError = classifySendError(error);
        if (isPermanent(sendError)) {
          doneRef.current = true;
          setRejected(sendError);
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    attempt();
    const timer = setInterval(attempt, TERMINATED_RETRY_INTERVAL_MS);
    window.addEventListener('online', attempt);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', attempt);
    };
  }, [enabled, stopped]);

  return { sent, rejected };
}
